import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import "../home/homeRedesign.css";
import "./monthlyReportsNew.css";
import CountUp from "react-countup";
import { getMonthlyReports } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const QUARTERS = [
  { id: "q1", label: "Quarter One", months: ["January", "February", "March"] },
  { id: "q2", label: "Quarter Two", months: ["April", "May", "June"] },
  { id: "q3", label: "Quarter Three", months: ["July", "August", "September"] },
  { id: "q4", label: "Quarter Four", months: ["October", "November", "December"] },
];

const EASE = [0.23, 1, 0.32, 1];

const IconPdf = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3v4a1 1 0 001 1h4" />
    <path d="M17 21h-10a2 2 0 01-2-2v-14a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </svg>
);

const MonthlyReports = () => {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getMonthlyReports();
        if (active) setModel(res?.data || null);
      } catch (e) {
        if (active) setModel(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const hero = model?.hero || {};
  const info = model?.infoSection || {};

  // Build a { [year]: reports[] } map from the backend year groups.
  const reportsData = useMemo(() => {
    if (!model?.years) return {};
    return model.years.reduce((acc, y) => {
      if (y?.year) acc[y.year] = y.reports || [];
      return acc;
    }, {});
  }, [model]);

  const YEARS = useMemo(
    () => Object.keys(reportsData).sort((a, b) => Number(b) - Number(a)),
    [reportsData]
  );

  // Default the active year to the most recent one once data arrives.
  useEffect(() => {
    if (!activeYear && YEARS.length) setActiveYear(YEARS[0]);
  }, [YEARS, activeYear]);

  const yearReports = reportsData[activeYear] || [];
  const reportByMonth = useMemo(
    () => Object.fromEntries(yearReports.map((r) => [r.month, r])),
    [yearReports]
  );

  const latestReport = yearReports[yearReports.length - 1];
  const totalReports = yearReports.length;
  const totalAllReports = useMemo(
    () => Object.values(reportsData).reduce((s, arr) => s + arr.length, 0),
    [reportsData]
  );

  if (loading) {
    return (
      <div className="mrpt-page">
        <Header parentMenu="academics" topbarEnable="enable" />
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <p style={{ color: "#53545b", fontSize: "1.1rem" }}>Loading…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="mrpt-page">
        <Header parentMenu="academics" topbarEnable="enable" />
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "8px" }}>
          <h2 style={{ color: "#0e2455" }}>Monthly Reports unavailable</h2>
          <p style={{ color: "#53545b" }}>Please try again later.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="mrpt-page">
      <Header parentMenu="academics" topbarEnable="enable" />

      <PageBanner
        title={hero.title || "Monthly Institutional Reports"}
        eyebrow={hero.eyebrow || "Institutional Transparency"}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      />

      {/* Impact stats — floating card (matches the Alumni page) */}
      <div className="mrpt-stats">
        <div className="container">
          <div className="mrpt-stats__inner">
            <div className="mrpt-stats__item">
              <span className="mrpt-stats__num">
                <CountUp end={YEARS.length} duration={2} enableScrollSpy scrollSpyOnce />
              </span>
              <span className="mrpt-stats__label">Academic Years Archived</span>
            </div>
            <div className="mrpt-stats__item">
              <span className="mrpt-stats__num">
                <CountUp end={totalAllReports} duration={2.2} enableScrollSpy scrollSpyOnce /><span>+</span>
              </span>
              <span className="mrpt-stats__label">Published Reports</span>
            </div>
            <div className="mrpt-stats__item">
              <span className="mrpt-stats__num">{activeYear || latestReport?.title}</span>
              <span className="mrpt-stats__label">Current Reporting Year</span>
            </div>
          </div>
        </div>
      </div>

      {/* Archive registry */}
      <section className="mrpt-archive">
        <div className="container">
          <div className="mrpt-archive__inner">
            {/* Vertical year rail */}
            <aside className="mrpt-years" aria-label="Select academic year">
              <span className="mrpt-years__label">Academic Year</span>
              <ul className="mrpt-years__list">
                {YEARS.map((year) => (
                  <li key={year}>
                    <button
                      type="button"
                      className={`mrpt-years__btn${activeYear === year ? " is-active" : ""}`}
                      onClick={() => setActiveYear(year)}
                      aria-pressed={activeYear === year}
                    >
                      <span className="mrpt-years__year">{year}</span>
                      <span className="mrpt-years__count">{reportsData[year].length} docs</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main document panel */}
            <div className="mrpt-panel">
              <div className="mrpt-panel__head">
                <div className="mrpt-panel__head-row">
                  <div>
                    <h2 className="mrpt-panel__title">Report Registry — {activeYear}</h2>
                    <p className="mrpt-panel__meta">
                      <strong>{totalReports}</strong> report{totalReports !== 1 ? "s" : ""} available
                      {latestReport && <> · Latest: <strong>{latestReport.title}</strong></>}
                    </p>
                  </div>
                  <span className="mrpt-panel__badge">
                    <span className="mrpt-panel__badge-dot" aria-hidden="true" />
                    PDF archive
                  </span>
                </div>

                {/* 12-month availability strip */}
                <div className="mrpt-cal" role="list" aria-label={`${activeYear} monthly availability`}>
                  {MONTHS.map((month, idx) => {
                    const report = reportByMonth[month];
                    if (report) {
                      return (
                        <a
                          key={month}
                          href={mediaUrl(report.pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mrpt-cal__cell mrpt-cal__cell--available"
                          role="listitem"
                          title={report.title}
                        >
                          <span className="mrpt-cal__abbr">{MONTH_ABBR[idx]}</span>
                          <span className="mrpt-cal__dot" aria-hidden="true" />
                        </a>
                      );
                    }
                    return (
                      <div
                        key={month}
                        className="mrpt-cal__cell mrpt-cal__cell--empty"
                        role="listitem"
                        aria-label={`${month} — not available`}
                      >
                        <span className="mrpt-cal__abbr">{MONTH_ABBR[idx]}</span>
                        <span className="mrpt-cal__dot" aria-hidden="true" />
                      </div>
                    );
                  })}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeYear}
                  className="mrpt-quarters"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: EASE }}
                >
                  {QUARTERS.map((quarter) => {
                    const quarterReports = quarter.months
                      .map((m) => reportByMonth[m])
                      .filter(Boolean);

                    return (
                      <div key={quarter.id} className="mrpt-quarter">
                        <div className="mrpt-quarter__head">
                          <span className="mrpt-quarter__tag">{quarter.id.toUpperCase()}</span>
                          <p className="mrpt-quarter__label">{quarter.label}</p>
                          <span className="mrpt-quarter__count">
                            {quarterReports.length} of {quarter.months.length} months
                          </span>
                        </div>

                        {quarterReports.length > 0 ? (
                          <ul className="mrpt-registry">
                            {quarterReports.map((item, idx) => (
                              <li key={item.title || `${quarter.id}-${idx}`}>
                                <a
                                  href={mediaUrl(item.pdf)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mrpt-row"
                                >
                                  <span className="mrpt-row__index">
                                    {String(idx + 1).padStart(2, "0")}
                                  </span>
                                  <span className="mrpt-row__body">
                                    <span className="mrpt-row__title">{item.title}</span>
                                    <span className="mrpt-row__sub">Monthly institutional activity report</span>
                                  </span>
                                  <span className="mrpt-row__action">
                                    <IconPdf />
                                    Open PDF
                                  </span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mrpt-quarter__empty">No reports published for this quarter in {activeYear}.</p>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Context band */}
      {(info.heading || info.text || (info.points && info.points.length > 0)) && (
        <section className="mrpt-info">
          <div className="container">
            <div className="mrpt-info__box">
              <div>
                <h3 className="mrpt-info__heading">{info.heading}</h3>
                <p className="mrpt-info__text">{info.text}</p>
              </div>
              <ul className="mrpt-info__list">
                {(info.points || []).map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default MonthlyReports;
